import React, {useEffect} from 'react';
import type {SetOptional} from 'type-fest';
import {Checkbox, FormControlLabel} from '@mui/material';
import {
	FormType,
	getInputFormObject,
	LegacyFormTextField,
} from '@netx/core-components/Form';
import {ScrollBorder} from '@netx/core-components/ScrollBorder';
import {styled} from '@netx/core-components/Theme';
import {CUSTOM_ACTION_COLUMN_LABEL, CUSTOM_ACTION_LABEL} from '../../locales';
import {CustomActionField, type ICustomAction} from '../../schema';
import type {CustomActionFormData} from '../../types';
import {CustomActionEditAccess} from '../CustomActionEditAccess';
import {
	CustomActionEndpointPayloadUrl,
	CustomActionEndpointTargetUrl,
} from '../CustomActionEndpoint';

const CustomActionFormRoot = styled('div')(({theme}) => ({
	...theme.mixins.flexFillColumn,
	overflow: 'hidden',
}));

const CustomActionFormHeader = styled('div')(({theme}) => ({
	display: 'flex',
	flex: '0 0 auto',
	justifyContent: 'flex-end',
	width: '100%',
}));

const CustomActionInputWrapper = styled('div')(({theme}) => ({
	marginTop: theme.spacing(1),
	marginBottom: theme.spacing(1),
}));

export type CustomActionForm = FormType<CustomActionFormData>;

export interface CustomActionEditProps
	extends React.DetailedHTMLProps<
		React.HTMLAttributes<HTMLDivElement>,
		HTMLDivElement
	> {
	form: CustomActionForm;
	customAction?: SetOptional<Omit<ICustomAction, 'secret'>, 'id'>;
}

export const CustomActionEditPrimitive = ({
	form,
	customAction,
	...props
}: CustomActionEditProps) => {
	const inputForm = getInputFormObject(form);

	useEffect(() => {
		const unregisters = [
			form.register(CustomActionField.enabled),
			form.register(CustomActionField.userSelector),
		];
		return () => {
			unregisters.forEach((unregister) => unregister());
		};
	}, []);

	return (
		<CustomActionFormRoot {...props}>
			<CustomActionFormHeader>
				<FormControlLabel
					checked={form.values[CustomActionField.enabled]}
					control={<Checkbox />}
					// because why not just change the name of a thing in one context?
					label={CUSTOM_ACTION_LABEL.ENABLED}
					onChange={(event, checked) => form.setValue('enabled', checked)}
				/>
			</CustomActionFormHeader>

			<ScrollBorder contentPadding={'0 16px'}>
				<CustomActionInputWrapper>
					<LegacyFormTextField
						autoFocus={customAction == null}
						inputProps={{
							maxLength: 25,
						}}
						error={form.errors[CustomActionField.name]}
						form={inputForm}
						fullWidth
						helperText={CUSTOM_ACTION_LABEL.NAME_HELP}
						label={CUSTOM_ACTION_COLUMN_LABEL[CustomActionField.name]}
						name={CustomActionField.name}
						required
						value={form.values[CustomActionField.name]}
					/>
				</CustomActionInputWrapper>

				<CustomActionInputWrapper>
					<CustomActionEndpointPayloadUrl
						customActionId={customAction?.id}
						changed={form.changes[CustomActionField.payloadUrl]}
						error={form.errors[CustomActionField.payloadUrl]}
						form={inputForm}
						fullWidth
						helperText={CUSTOM_ACTION_LABEL.PAYLOAD_URL_HELP}
						required
						value={form.values[CustomActionField.payloadUrl]}
					/>
				</CustomActionInputWrapper>

				<CustomActionInputWrapper>
					<CustomActionEndpointTargetUrl
						customActionId={customAction?.id}
						changed={form.changes[CustomActionField.targetUrl]}
						error={form.errors[CustomActionField.targetUrl]}
						form={inputForm}
						fullWidth
						helperText={CUSTOM_ACTION_LABEL.TARGET_URL_HELP}
						value={form.values[CustomActionField.targetUrl]}
					/>
				</CustomActionInputWrapper>

				<CustomActionEditAccess
					userSelector={form.values.userSelector}
					onChange={(userSelector) =>
						form.setValue('userSelector', userSelector)
					}
				/>
			</ScrollBorder>
		</CustomActionFormRoot>
	);
};

export const CustomActionEdit = CustomActionEditPrimitive;

export default CustomActionEdit;
